import "dayjs/locale/th";
import "dayjs/locale/en";

import { enUS } from "date-fns/locale";
import { type Dayjs } from "dayjs";
import mainDayjs from "dayjs";
import i18next from "i18next";

import dayjs from "@/utils/dayjs";

const BANGKOK_TIMEZONE = "Asia/Bangkok";

export const toDayjs = (date: number | Date | string | Dayjs): Dayjs => {
  return dayjs.isDayjs(date) ? date : dayjs(date);
};

export const parseDayjs = (
  date?: number | string | Date | Dayjs | null | undefined,
): Dayjs | undefined => {
  if (!date) return undefined;
  return dayjs.isDayjs(date) ? date : dayjs(date);
};

export const convertLocaleToStringFunction = (locale: Locale): string => {
  // Assuming the locale object has a 'code' property
  if (locale && locale.code) {
    return locale.code;
  }

  // If the locale does not have a valid code, return a default or throw an error
  return "en"; // Default to English if no valid locale is provided
};

export const convertLocaleToString = (locale: Locale | string): string => {
  // Use unknown to bypass type checking
  let localeString: string;
  if (typeof locale !== "string") {
    localeString = convertLocaleToStringFunction(locale);
  } else {
    localeString = locale;
  }

  return localeString;
};

export const gregorianToBuddhist = (gregorianYear: number): number => {
  // The offset between Gregorian and Buddhist years
  const buddhistOffset = 543;

  // Calculate the Buddhist year
  const buddhistYear = gregorianYear + buddhistOffset;

  return buddhistYear;
};

function formatDateString(date: Date | Dayjs, formatDate: string): string {
  const dateDayjs = toDayjs(date);

  // Extract values from the dayjs instance
  const year = dateDayjs.year();
  const month = (dateDayjs.month() + 1).toString().padStart(2, "0"); // month() is zero-based in dayjs
  const day = dateDayjs.date().toString().padStart(2, "0");

  // Replace custom format placeholders with the extracted values
  return formatDate
    .replace(/yyyy/g, year.toString())
    .replace(/MM/g, month)
    .replace(/dd/g, day);
}

export const convertFormatDateTimeDisplayTime = (
  inputDate?: string,
  locale?: string,
): string => {
  if (inputDate) {
    return dayjs(inputDate)
      .locale(locale ?? "th")
      .format("HH:mm");
  }
  return "";
};
export const parseTimeString = (inputString: string, display: string) => {
  const hours = inputString.match(/\d+H/);
  const minutes = inputString.match(/\d+M/);
  if (display === "h") {
    return hours ? hours[0] : null;
  }
  return minutes ? minutes[0] : null;
};

export function formatDateToString(
  date: Date | Dayjs,
  customOptions?: Intl.DateTimeFormatOptions,
  formatDate?: string,
): string {
  const dateDayjs = toDayjs(date);

  // Use custom format if provided
  if (formatDate) {
    return formatDateString(dateDayjs, formatDate);
  }

  // Default format using dayjs format string
  const defaultFormat = "DD/MM/YYYY"; // Default format for "en-GB"
  let formatString = defaultFormat;

  // Construct format string based on Intl.DateTimeFormatOptions
  if (customOptions) {
    const { year, month, day } = customOptions;
    formatString = `${day === "2-digit" ? "DD" : "D"}/${month === "2-digit" ? "MM" : "M"}/${year === "numeric" ? "YYYY" : "YY"}`;
  }

  // Use dayjs's format function to apply the format string
  return dateDayjs.format(formatString);
}

// 07 เม.ย. 2030
export function formatDateToFormatString(
  date: Date | Dayjs | null,
  locale: string | Locale = "en",
  formatDate: string = "DD MMM YYYY",
): string {
  if (!date) return "";
  const localeString =
    typeof locale !== "string" ? convertLocaleToString(locale) : locale;
  const dateDayjs = toDayjs(date).locale(localeString);
  return dateDayjs.format(formatDate);
}

export const calculateTimeDifference = (
  startDateString: string,
  endDateString: string,
  display: string,
) => {
  // Parse the input strings into Dayjs objects
  const startDate = dayjs(startDateString);
  const endDate = dayjs(endDateString);

  // Calculate the difference in milliseconds
  const timeDifference = endDate.diff(startDate);

  // Calculate hours and minutes using dayjs methods
  const hours = Math.floor(timeDifference / (1000 * 60 * 60));
  const minutes = Math.floor((timeDifference % (1000 * 60 * 60)) / (1000 * 60));

  // Return the appropriate time unit
  if (display === "h") {
    return hours;
  }
  if (display !== "h" && minutes > 0) {
    return minutes;
  }
  return 0; // Default to 0 if no valid display option or no positive minute difference
};

export const convertFormatDateTimeDisplayDate = (
  inputDate?: string,
  locale?: string,
  formatDate?: string,
): string => {
  if (inputDate) {
    return dayjs(inputDate)
      .locale(locale ?? "th")
      .format(formatDate ?? "D dd MMM");
  }
  return "";
};

export function formatDateForQuery(date: Date | Dayjs): string {
  return formatDateToString(toDayjs(date), undefined, "yyyy-MM-dd");
}

// Format short date
const formatShortDate = (date: Date | Dayjs, locale: string = "en"): string => {
  const dateDayjs = toDayjs(date);
  return dateDayjs.locale(locale).format("D MMMM");
};

// Format short month
export const formatShortMonth = (
  date: Date | Dayjs,
  locale: string | Locale = "en",
): string => {
  const dateDayjs = toDayjs(date);
  return dateDayjs.locale(convertLocaleToString(locale)).format("D MMM");
};

// Format month and year
export const formatMonthYear = (
  date: Date | Dayjs,
  locale: string | Locale = "en",
): string => {
  const dateDayjs = toDayjs(date);
  const month = dateDayjs.locale(convertLocaleToString(locale)).format("MMMM");
  const year = dateDayjs.year();
  return `${month} ${year}`;
};

// Format short month and year
export const formatShortMonthYear = (
  date: Date | Dayjs,
  locale: string | Locale = "en",
): string => {
  const dateDayjs = toDayjs(date);
  const month = dateDayjs.locale(convertLocaleToString(locale)).format("MMM");
  const year = dateDayjs.year();
  return `${month} ${year}`;
};

// Format short date range
export const formatShortDateRange = (
  dateArray: (Date | Dayjs | undefined)[],
  locale: Locale = enUS,
  isHotel?: boolean,
  custom?: string,
): string => {
  const localeString: string = convertLocaleToString(locale); // Convert Locale to string

  if (dateArray.length < 2) return "";

  const [d1, d2] = dateArray;

  if (!d1 || !d2) {
    return "";
  }

  const date1 = toDayjs(d1);
  const date2 = toDayjs(d2);

  const formattedDate1 = formatShortDate(date1, localeString);
  const formattedDate2 = formatShortDate(date2, localeString);

  const nights = date2.diff(date1, "day");
  const textEng = nights > 1 ? "nights" : "night";
  const nightText = localeString && localeString === "th" ? "คืน" : textEng;
  const nightsText = `(${nights} ${nightText})`;
  if (custom === "single") {
    return `${formattedDate1}`;
  }
  return `${formattedDate1} ${date1.year()} - ${formattedDate2} ${date2.year()} ${
    isHotel ? nightsText : ""
  }`;
};

// Format day, month, and year
export const dateFormatDayMonthYear = (
  date: Date | Dayjs | undefined,
  locale: string | Locale = "en",
  dateFormat?: string,
) => {
  try {
    const isStr = typeof locale === "string";
    const localStr = isStr ? locale : locale?.code;
    const dayFormat =
      dateFormat ||
      (localStr?.includes("th") ? "dd DD MMM YYYY" : "ddd DD MMM YYYY");

    const newDate = date || dayjs();
    const dateDayjs = toDayjs(newDate);
    return dateDayjs.locale(convertLocaleToString(locale)).format(dayFormat);
  } catch (error) {
    return "";
  }
};
export const convertFormatDateTime = (
  inputDate?: string,
  locale: string | Locale = "en",
): string => {
  if (inputDate) {
    const date = dayjs(inputDate); // Use dayjs to parse the ISO string
    const formattedDate = date
      .locale(convertLocaleToString(locale))
      .format("ddd DD MMM YYYY HH:mm");
    return formattedDate;
  }
  return "";
};

export const stringToDate = (dateString?: string | null): Dayjs | null => {
  if (!dateString) return null;
  const dateObject = mainDayjs(dateString).tz(BANGKOK_TIMEZONE, true); // Use dayjs to parse the string

  // Check if the conversion was successful
  if (!dateObject.isValid()) {
    return null; // Return null if invalid
  }

  return dateObject; // Return the dayjs object
};

export const addDaysToDate = (
  daysToAdd: number,
  date: Date | Dayjs = dayjs(),
): Dayjs => {
  const dateDayjs = toDayjs(date); // Convert to dayjs
  return dateDayjs.clone().add(daysToAdd, "day"); // Add days using dayjs
};

export const nextMonth = (date: Date | Dayjs): Dayjs => {
  const currentDate = toDayjs(date); // Convert to dayjs
  return currentDate.clone().add(1, "month").startOf("month"); // Get the first day of the next month
};

// SECTION - Parse ISO date
export const parseISODuration = ({
  durationString,
  returnHours = false,
}: {
  durationString: string;
  returnHours: boolean;
}): any => {
  const durationRegex = /P(?:(\d+)D)?T?(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/;
  const matches = durationString.match(durationRegex);

  if (!matches) {
    throw new Error("Invalid ISO 8601 duration string");
  }

  const days = parseInt(matches[1] || "0", 10);
  const hours = parseInt(matches[2] || "0", 10);
  const minutes = parseInt(matches[3] || "0", 10);
  const seconds = parseInt(matches[4] || "0", 10);

  if (returnHours) {
    const totalHours = days * 24 + hours + minutes / 60 + seconds / 3600;
    return totalHours;
  }
  return {
    days,
    hours,
    minutes,
    seconds,
  };
};

export const formatDateForDateRangePicker = (
  date?: Dayjs | null,
  local: Locale = enUS,
  isHideYear: boolean = false,
  isHideDay: boolean = false,
): string => {
  if (!date) return "";

  // Convert Locale to string
  const localeString: string = convertLocaleToString(local);

  // Convert to Dayjs object
  const dateDayjs = dayjs(date).locale(localeString); // Set the locale here
  const isValidDate = dayjs(dateDayjs).isValid();
  if (!isValidDate) return "";

  const dayFormat = local?.code === "th" ? "dd" : "ddd";
  const day = dateDayjs.date();
  const month = dateDayjs.format("MMM"); // Only format with the pattern
  const year = dateDayjs.year();
  const dayName = dateDayjs.format(dayFormat); // Format for day name

  const formattedDate = `${isHideDay ? "" : dayName} ${
    day < 10 ? "0" : ""
  }${day} ${month} ${isHideYear ? "" : year}`;
  return formattedDate;
};

export const getMonthYearLabel = (dateString: string, lang: string) => {
  dayjs.locale(lang);

  return dayjs(dateString).format("MMMM YYYY");
};

// Sat 23 Nov 2024, ส. 23 พ.ย. 2024
export function formatDateByLocale(
  date: Date | Dayjs | null,
  locale: String | Locale = enUS,
  options?: { hideYear?: boolean; hideMonth?: boolean },
) {
  const { hideYear, hideMonth } = options || {};

  if (!date) return "";

  let formatString = "ddd D MMM YYYY";

  if (locale === "th") {
    formatString = "dd D MMM YYYY";
  }

  if (hideYear) {
    formatString = formatString.replace("YYYY", "").trim();
  }

  if (hideMonth) {
    formatString = formatString.replace("MMM", "").replace(/ +/g, " ").trim();
  }

  return dayjs(date).format(formatString);
}

type KeyOfFormat = "diffYear" | "diffMonth" | "diffDay";
interface FormatDurationRangeInterface {
  dateStart?: Dayjs;
  dateEnd?: Dayjs;
  formatDate?: {
    [key in KeyOfFormat]: {
      start: string;
      end: string;
    };
  };
}
export function formatDurationRange({
  dateStart,
  dateEnd,
  formatDate = {
    diffYear: { start: "DD MMM YYYY", end: "DD MMM YYYY" },
    diffMonth: { start: "DD MMM", end: "DD MMM YYYY" },
    diffDay: { start: "DD", end: "DD MMM YYYY" },
  },
}: FormatDurationRangeInterface) {
  const { languages } = i18next;
  const lang = (languages[0] || "en-us").slice(0, 2);
  dayjs.locale(lang);

  const start = dayjs.isDayjs(dateStart) ? dateStart : dayjs();
  const end = dayjs.isDayjs(dateEnd) ? dateEnd : dayjs().add(1, "day");
  const isNoteSameYear = end.year() === start.year();
  const isSameMonth = end.month() === start.month();
  const isSameDay = end.day() === start.day();

  const { diffYear, diffDay, diffMonth } = formatDate;

  if (!isNoteSameYear) {
    const startDate = formatDateToFormatString(start, lang, diffYear.start);
    const endDate = formatDateToFormatString(end, lang, diffYear.end);
    return {
      text: `${startDate} - ${endDate}`,
    };
  }

  if (!isSameMonth) {
    const startDate = formatDateToFormatString(start, lang, diffMonth.start);
    const endDate = formatDateToFormatString(end, lang, diffMonth.end);
    return {
      text: `${startDate} - ${endDate}`,
      dateStart: start,
      dateEnd: end,
    };
  }

  if (isSameDay) {
    const endDate = formatDateToFormatString(end, lang, diffDay.end);
    return {
      text: `${endDate}`,
      dateStart: start,
      dateEnd: end,
    };
  }

  const startDate = formatDateToFormatString(start, lang, diffDay.start);
  const endDate = formatDateToFormatString(end, lang, diffDay.end);
  return {
    text: `${startDate} - ${endDate}`,
    dateStart: start,
    dateEnd: end,
  };
}
export function removeTimezoneOffset(dateString) {
  if (!dateString) {
    return "";
  }
  return dateString.replace(/([+-]\d{4})$/, "");
}
